import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HistoricalDataPoint, PreSignedUrlResponse, XylemDevice } from '../models/dashboard-data.model';

/**
 * Historical Data Service
 * Handles fetching historical time-series data with progress tracking
 */
@Injectable({
  providedIn: 'root'
})
export class HistoricalDataService {
  // Progress tracking (0-100%)
  private loadingProgressSubject = new BehaviorSubject<number>(0);
  loadingProgress$ = this.loadingProgressSubject.asObservable();
  
  // Loading state
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();
  
  // Error state
  private errorSubject = new BehaviorSubject<string | null>(null);
  error$ = this.errorSubject.asObservable();
  
  constructor(private http: HttpClient) {}
  
  /**
   * Fetch historical data for a date range
   * @param fromDate - Start date
   * @param toDate - End date
   * @returns Promise with historical data points
   */
  async getHistoricalData(fromDate: Date, toDate: Date): Promise<HistoricalDataPoint[]> {
    try {
      this.isLoadingSubject.next(true);
      this.loadingProgressSubject.next(10);
      this.errorSubject.next(null);
      
      const params = new HttpParams()
        .set('from-date', fromDate.toISOString())
        .set('to-date', toDate.toISOString());
      
      this.loadingProgressSubject.next(30);
      
      // Fetch historical data directly from Xylem API
      const historicalData = await firstValueFrom(
        this.http.get<HistoricalDataPoint[]>(
          environment.aws.historicalData,
          { params }
        )
      );
      
      this.loadingProgressSubject.next(90);
      
      // Process data (if needed)
      const processedData = this.processHistoricalData(historicalData);
      
      this.loadingProgressSubject.next(100);
      this.isLoadingSubject.next(false);
      
      return processedData;
      
    } catch (error: any) {
      this.errorSubject.next(error.message || 'Failed to load historical data');
      this.isLoadingSubject.next(false);
      this.loadingProgressSubject.next(0);
      console.error('Historical data fetch error:', error);
      throw error;
    }
  }
  
  /**
   * Get historical data using direct API endpoint (alternative method)
   */
  async getHistoricalDataDirect(fromDate: Date, toDate: Date): Promise<HistoricalDataPoint[]> {
    try {
      this.isLoadingSubject.next(true);
      this.loadingProgressSubject.next(20);
      this.errorSubject.next(null);
      
      const params = new HttpParams()
        .set('from-date', fromDate.toISOString())
        .set('to-date', toDate.toISOString());
      
      this.loadingProgressSubject.next(50);
      
      const data = await firstValueFrom(
        this.http.get<HistoricalDataPoint[]>(
          environment.aws.historicalData,
          { params }
        )
      );
      
      this.loadingProgressSubject.next(90);
      
      const processedData = this.processHistoricalData(data);
      
      this.loadingProgressSubject.next(100);
      this.isLoadingSubject.next(false);
      
      return processedData;
      
    } catch (error: any) {
      this.errorSubject.next(error.message || 'Failed to load historical data');
      this.isLoadingSubject.next(false);
      this.loadingProgressSubject.next(0);
      console.error('Historical data fetch error:', error);
      throw error;
    }
  }
  
  /**
   * Process historical data — handles two possible API response shapes:
   * Shape A: HistoricalDataPoint[] — true time-series (from /history endpoint)
   * Shape B: XylemDevice[] — flat snapshot (from /data endpoint without live=true)
   */
  private processHistoricalData(data: any[]): HistoricalDataPoint[] {
    if (!data?.length) return [];

    // Shape A: already HistoricalDataPoint[] — has 'devices' array property
    if ('devices' in data[0] && Array.isArray((data[0] as any).devices)) {
      return (data as HistoricalDataPoint[]).sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
    }

    // Shape B: XylemDevice[] snapshot — extract per-group timestamps
    const devices = data as XylemDevice[];
    const timestamps = new Set<string>();
    devices.forEach(d =>
      d.deviceData?.groups?.forEach(g => { if (g.timestamp) timestamps.add(g.timestamp); })
    );

    if (timestamps.size === 0) {
      return [{ timestamp: new Date().toISOString(), devices }];
    }

    // Build one synthetic HistoricalDataPoint per unique group timestamp
    return Array.from(timestamps)
      .sort()
      .map(ts => ({ timestamp: ts, devices }));
  }
  
  /**
   * Chunk data for rendering (500 points at a time)
   * Used to prevent UI freezing with large datasets
   */
  chunkData(data: HistoricalDataPoint[], chunkSize: number = 500): HistoricalDataPoint[][] {
    const chunks: HistoricalDataPoint[][] = [];
    for (let i = 0; i < data.length; i += chunkSize) {
      chunks.push(data.slice(i, i + chunkSize));
    }
    return chunks;
  }
  
  /**
   * Reset progress and error states
   */
  reset(): void {
    this.loadingProgressSubject.next(0);
    this.isLoadingSubject.next(false);
    this.errorSubject.next(null);
  }
}
